/**
 * Board text a button covers: check_overlaps asks on each page of a deck at rest. A caption or footer laid out
 * at the Nav row's height sits behind (or over) Previous/Next and cannot be read.
 */
import type { MeasuredScene } from 'trace-viewer/quality';

type Box = NonNullable<MeasuredScene['nodes'][number]['box']>;
type V3 = Box['center'];

/** Text this close to a button along its normal, metres, lies on the same board face (behind it or over it). */
const NEAR_M = 0.05;
/** In-plane overlap that counts, metres: a layout unit. */
const MIN_M = 0.001;

const dot = (a: V3, b: V3): number => a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
const reach = (box: Box, axis: V3): number => box.axes.reduce((n, a, i) => n + Math.abs(dot(a, axis)) * box.half[i], 0);
const under = (path: string, root: string): boolean => path === root || path.startsWith(`${root}/`);

export interface TextUnderButton {
    text: string;
    button: string;
    /** Overlap across and up the text, metres. */
    overlap: [number, number];
}

/**
 * Each text (by path) that a button covers, with the overlap in the text's plane. `buttons` are the paths of the
 * pressable objects; a button's box is what draws under it. Text under a button's path is its label and is not
 * judged; a label placed outside it is judged like any other text. `skip` leaves out text that is not board
 * content (outside the deck, or the model's parts and cues).
 */
export function textUnderButtons(scene: MeasuredScene, buttons: readonly string[], skip: (path: string) => boolean = () => false): TextUnderButton[] {
    const drawn = buttons.map(button => ({ button, boxes: scene.nodes.flatMap(n => n.box && (n.kind === 'panel' || n.kind === 'solid') && under(n.path, button) ? [n.box] : []) }))
        .filter(b => b.boxes.length);
    const found: TextUnderButton[] = [];
    for (const t of scene.nodes) {
        if (t.kind !== 'text' || !t.box || t.text?.color?.alpha === 0 || skip(t.path)) continue;
        const box = t.box;
        for (const { button, boxes } of drawn) {
            if (under(t.path, button)) continue;
            // The button in the text's own axes: one interval per axis around everything it draws.
            const lo = [Infinity, Infinity, Infinity], hi = [-Infinity, -Infinity, -Infinity];
            for (const b of boxes) {
                const d: V3 = [b.center[0] - box.center[0], b.center[1] - box.center[1], b.center[2] - box.center[2]];
                box.axes.forEach((axis, k) => {
                    const at = dot(d, axis), r = reach(b, axis);
                    lo[k] = Math.min(lo[k], at - r); hi[k] = Math.max(hi[k], at + r);
                });
            }
            if (lo[2] > NEAR_M + box.half[2] || hi[2] < -NEAR_M - box.half[2]) continue;
            const overlap = [0, 1].map(k => Math.min(box.half[k], hi[k]) - Math.max(-box.half[k], lo[k])) as [number, number];
            if (overlap.every(o => o > MIN_M)) found.push({ text: t.path, button, overlap });
        }
    }
    return found;
}
